import React, { useState } from 'react';
import { Flex } from '@withjoy/joykit';
import type { CSSProperties, FC } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';
import { baseWidth, baseHeight, desiredPadding, desiredPaddingMobile, pageEdgeMargin, pageEdgeMarginMobile, responsiveSettings } from '../ImagineYourCardRoot/cardConstants';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { BOX_SHADOW_16 } from '@apps/card/constants/boxShadow';

// Roles:
// 1) Parent controls width.
// 2) Child controls aspect ratio (via height/width).
//    The Parent should follow with height auto.
// 3) This controls the scale and sets it on the child.
type PageScalerProps = Pick<CSSProperties, 'position'> & {
  onScaleChange?: (scale: number) => void;
  colsOverride?: number | undefined;
  widthCoeff?: number | undefined;
  useMargin?: boolean | undefined;
  onReady?: () => void | undefined;
  marginOverride?: number | undefined;
  hideShadow?: boolean;
};

const maxWidth = 600;
const maxScale = maxWidth / baseWidth;

export const ResponsivePageScaler: FC<PageScalerProps> = props => {
  const { children, onReady, onScaleChange, colsOverride, widthCoeff, useMargin, hideShadow, marginOverride, ...cssProps } = props;
  const columns = useResponsive(responsiveSettings, 1)[0];
  const mobile = columns <= 2;
  const [isScaled, setIsScaled] = useState(false);
  const [isSettled, setIsSettled] = useState(false);

  const gap = (mobile ? desiredPaddingMobile : desiredPadding) * 2;
  const pageMargin = (marginOverride ?? mobile) ? pageEdgeMarginMobile : pageEdgeMargin;
  const totalWidth = baseWidth + gap;

  const ref = useRef<HTMLDivElement>(null);
  // Latest-value refs so new callback identities or the isScaled flip don't tear down the observer.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onScaleChangeRef = useRef(onScaleChange);
  onScaleChangeRef.current = onScaleChange;
  const isScaledRef = useRef(isScaled);
  isScaledRef.current = isScaled;
  const measureRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    const self = ref.current;
    if (!self) return;

    const measure = () => {
      const child = self.firstElementChild as HTMLElement;
      if (!child) return;

      const frameWidth = document.body.clientWidth * (widthCoeff || 1) - (useMargin ? pageMargin * 2 : 0);
      const avaliableWidth = frameWidth / (colsOverride || columns);
      const scale = Math.min(avaliableWidth / totalWidth, maxScale);
      self.style.height = `${baseHeight * scale}px`;
      self.style.width = `${child.clientHeight * scale * (5 / 7)}px`;
      child.style.position = 'absolute';
      child.style.scale = `${scale}`;
      onScaleChangeRef.current?.(scale);

      if (!isScaledRef.current) {
        setIsScaled(true);
      }

      if (isScaledRef.current && self.style.display === 'flex') {
        setIsSettled(true);
        onReadyRef.current?.();
      }
    };

    // Measure before paint so the component settles in the same commit. The observer's first
    // delivery is deferred and only catches later body resizes.
    measure();
    measureRef.current = measure;
    const resizeObserver = createSafeResizeObserver(measure, { throttle: false });
    resizeObserver.observe(document.body);
    return () => {
      measureRef.current = null;
      resizeObserver.disconnect();
    };
  }, [colsOverride, columns, gap, pageMargin, ref, totalWidth, useMargin, widthCoeff]);

  // The first measure runs while hidden with display: none, where the child has no height. Once
  // isScaled shows it, re-measure before paint to size it and settle.
  useLayoutEffect(() => {
    if (isScaled) measureRef.current?.();
  }, [isScaled]);

  return (
    <Flex
      ref={ref}
      position="relative"
      width="100%" // Match parent width by convention.
      {...cssProps} // Allows caller to override position relative.
      alignItems="center"
      justifyContent="center"
      style={{ boxShadow: hideShadow ? undefined : BOX_SHADOW_16, display: isScaled ? 'flex' : 'none', visibility: isSettled ? 'visible' : 'hidden' }} // Do not take up space until size is calculated && do not render until settled.
    >
      {children}
    </Flex>
  );
};
